import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  EditOutlined,
  ApartmentOutlined,
  Close,
  NotesOutlined,
  PaymentsOutlined,
  PersonOutlined,
  SaveOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormSection from "./FormSection";
import { dayStart, dayString } from "../utils/dates";
import { radius, tokens } from "../theme/theme";

/** Every field the save button insists on — unchanged from the old dialog. */
const REQUIRED = ["region", "deadline", "rooms", "cost"];

const EditClient = ({ onEditItem, onCloseDialog, regionSearchTerms, item }) => {
  const [dateFrom, setDateFrom] = useState(item.dateFrom);
  const [rooms, setRooms] = useState(item.rooms);
  const [cost, setCost] = useState(item.cost);
  const [currency, setCurrency] = useState(item.currency);
  const [region, setRegion] = useState(item.region);
  const [aboutClient, setAboutClient] = useState(item.aboutClient);
  const [deadline, setDeadline] = useState(item.deadline);
  const [urgency, setUrgency] = useState(item.urgency);
  const [personName, setPersonName] = useState(item.personName);
  const [phoneNumber, setPhoneNumber] = useState(item.phoneNumber);
  const [info, setInfo] = useState(item.info);
  const [displayDateFrom, setDisplayDateFrom] = useState(null);
  const [displayDateEdit, setDisplayDateEdit] = useState(new Date());

  // The old save button did nothing at all when a required field was empty.
  const [showErrors, setShowErrors] = useState(false);

  const values = {
    region: region,
    deadline: deadline,
    rooms: rooms,
    cost: cost,
  };
  const missing = REQUIRED.filter(function (key) {
    return values[key] === "" || values[key] === null;
  });

  const onSave = (e) => {
    e.preventDefault();
    item = {
      id: item.id,
      dateFrom: dateFrom,
      dateEdit: dayStart(displayDateEdit),
      rooms: rooms,
      cost: cost,
      currency: currency,
      region: region,
      aboutClient: aboutClient,
      deadline: deadline,
      urgency: urgency,
      personName: personName,
      phoneNumber: phoneNumber,
      info: info,
    };
    onEditItem(item);
    onCloseDialog();
  };

  const trySave = (e) => {
    if (missing.length === 0) {
      onSave(e);
    } else {
      setShowErrors(true);
    }
  };

  const noEnter = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const required = (value) => ({
    error: showErrors && value === "",
    helperText: showErrors && value === "" ? "Заповніть це поле" : " ",
  });

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={onCloseDialog}
      PaperProps={{ sx: { borderRadius: radius.lg } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid " + tokens.line,
          py: 2,
        }}
      >
        <EditOutlined sx={{ color: tokens.blue600 }} />
        <Box sx={{ flexGrow: 1, lineHeight: 1.2 }}>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
            Редагувати клієнта
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.ink400 }}>
            {[item.personName, item.phoneNumber].filter(Boolean).join(" · ") ||
              item.id}
          </Typography>
        </Box>
        <IconButton onClick={onCloseDialog} aria-label="Закрити">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: tokens.canvas }}>
        <Box component="form" noValidate autoComplete="off" sx={{ pt: 3 }}>
          <FormSection title="Клієнт" icon={<PersonOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-name"
                  label="Ім'я клієнта"
                  variant="outlined"
                  type="text"
                  value={personName}
                  onKeyPress={noEnter}
                  onChange={(e) => setPersonName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-phone"
                  label="Номер клієнта"
                  variant="outlined"
                  type="text"
                  value={phoneNumber}
                  onKeyPress={noEnter}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Запит" icon={<ApartmentOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="client-rooms"
                  label="Кімнат *"
                  variant="outlined"
                  type="number"
                  value={rooms}
                  onKeyPress={noEnter}
                  onChange={(e) => setRooms(e.target.value)}
                  fullWidth
                  {...required(rooms)}
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <Autocomplete
                  freeSolo
                  inputValue={region || ""}
                  options={regionSearchTerms.map((option) => option)}
                  onInputChange={(e, newValue) => setRegion(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Район *"
                      {...required(region)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Бюджет" icon={<PaymentsOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="client-cost"
                  label="Бажана ціна *"
                  variant="outlined"
                  type="number"
                  value={cost}
                  onKeyPress={noEnter}
                  onChange={(e) => setCost(e.target.value)}
                  fullWidth
                  {...required(cost)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  inputValue={currency || ""}
                  options={["грн", "$"]}
                  onInputChange={(e, newValue) => setCurrency(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Валюта"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    value={displayDateFrom}
                    onChange={(newValue) => {
                      setDisplayDateFrom(newValue);
                      if (newValue === null) setDateFrom("");
                      else
                        setDateFrom(dayString(newValue));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "дд.мм.рррр",
                        }}
                        onKeyPress={noEnter}
                        fullWidth
                        variant="outlined"
                        label="Дата заповнення"
                        helperText=" "
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            title="Терміновість"
            icon={<ScheduleOutlined fontSize="small" />}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <TextField
                  id="client-deadline"
                  label="Термін *"
                  variant="outlined"
                  type="number"
                  value={deadline}
                  onKeyPress={noEnter}
                  onChange={(e) => setDeadline(e.target.value)}
                  fullWidth
                  {...required(deadline)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    value={displayDateEdit}
                    onChange={(newValue) => setDisplayDateEdit(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "дд.мм.рррр",
                        }}
                        onKeyPress={noEnter}
                        fullWidth
                        variant="outlined"
                        label="Дата редагування"
                        helperText=" "
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  inputValue={urgency || ""}
                  options={["Висока", "Середня", "Низька"]}
                  onInputChange={(e, newValue) => setUrgency(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Терміновість"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Нотатки" icon={<NotesOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-about"
                  label="Про клієнта"
                  variant="outlined"
                  type="text"
                  value={aboutClient}
                  onChange={(e) => setAboutClient(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-info"
                  label="Додаткова інформація"
                  variant="outlined"
                  type="text"
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </FormSection>

          {showErrors && missing.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Заповніть обов'язкові поля, позначені зірочкою.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid " + tokens.line, gap: 1 }}
      >
        <Button variant="text" onClick={onCloseDialog}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<SaveOutlined />}
          onClick={trySave}
        >
          Зберегти зміни
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditClient;
